import { useState, useRef, useEffect } from 'react';
import { Award, Medal, ArrowRight, ExternalLink } from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import GlowingText from '../ui/GlowingText';
import { useIntersectionObserver } from '../../hooks/useOptimizedHooks';

const CertificatesSection = ({ certifications }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const sectionRef = useRef(null);
  const { entries, observe } = useIntersectionObserver({ threshold: 0.1 });
  
  useEffect(() => {
    if (sectionRef.current) {
      observe(sectionRef.current, 'certificates-section');
    }
  }, [observe]);
  
  useEffect(() => {
    const entry = entries['certificates-section'];
    if (entry?.isIntersecting) {
      setIsVisible(true);
    }
  }, [entries]);

  if (!certifications?.length) return null;

  return (
    <section 
      id="certificates" 
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
    >
      {/* Background with animated elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950"></div>
      
      {/* Animated decoration elements */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-violet-500/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-violet-500/30 to-transparent"></div>
      
      {/* Animated background shapes */}
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-violet-600/5 blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-blue-600/5 blur-3xl animate-pulse-slow" style={{animationDelay: '1s'}}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-indigo-600/5 blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      
      <div className="container mx-auto px-6 relative">
        {/* Section Header with animated elements */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="relative inline-block">
            <div className="absolute -top-6 -right-6 w-12 h-12 border-2 border-violet-500/20 rounded-full animate-spin-slow"></div>
            <div className="absolute -bottom-6 -left-6 w-8 h-8 border-2 border-blue-500/20 rounded-full animate-spin-slow" style={{animationDirection: 'reverse', animationDelay: '1s'}}></div>
            <GlowingText color="mixed" size="3xl" weight="bold" className="inline-flex items-center gap-3 relative z-10">
              <Medal className="w-9 h-9 text-violet-400" />
              Certifications & Achievements
            </GlowingText>
          </div>
          <p className="mt-6 text-neutral-400 text-lg max-w-2xl mx-auto">
            Professional qualifications and recognition from industry-leading organizations
          </p>
        </div>

        {/* Certificates Showcase */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {certifications.map((cert, index) => (
            <GlassCard
              key={cert.id}
              variant="bordered"
              glow={true}
              glowIntensity={activeCard === cert.id ? "strong" : "subtle"}
              hoverEffect="lift"
              className={`p-0 overflow-hidden transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ 
                animationDelay: `${index * 150}ms`,
                transform: `${activeCard === cert.id ? 'scale(1.03)' : 'scale(1)'}`,
                transitionDelay: `${index * 100}ms`
              }}
              onMouseEnter={() => setActiveCard(cert.id)}
              onMouseLeave={() => setActiveCard(null)}
            >
              {/* Top colored header */}
              <div className={`h-2 w-full bg-gradient-to-r ${cert.gradient}`}></div>
              
              <div className="p-6 relative">
                {/* Certificate content with improved layout */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${cert.gradient} 
                    flex items-center justify-center text-2xl shadow-lg transform transition-transform duration-500 ${activeCard === cert.id ? 'rotate-12 scale-110' : ''}`}>
                    {cert.icon}
                  </div>
                  <span className="text-sm font-medium py-1 px-3 rounded-full bg-neutral-800/50 text-violet-300">
                    {cert.year}
                  </span>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-violet-300 transition-colors">
                    {cert.title}
                  </h3>
                  <p className="text-sm text-violet-400 mb-4 flex items-center">
                    <Award className="w-4 h-4 mr-1 inline" />
                    {cert.organization}
                  </p>
                  <p className="text-sm text-neutral-400 leading-relaxed">
                    {cert.description}
                  </p>
                </div>
                
                {/* View credential button (decorative) */}
                <div className={`mt-5 pt-4 border-t border-neutral-800/50 transition-opacity duration-300 ${activeCard === cert.id ? 'opacity-100' : 'opacity-0'}`}>
                  <button className="text-sm text-violet-400 hover:text-violet-300 flex items-center gap-1 group">
                    <span>View Credential</span>
                    <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>

                {/* Decorative elements */}
                <div className={`absolute -bottom-4 -right-4 w-20 h-20 opacity-10 transition-all duration-500 ${activeCard === cert.id ? 'rotate-45 scale-125' : 'rotate-0'}`}>
                  <div className={`absolute bottom-0 right-0 w-full h-full rounded-tl-3xl 
                    bg-gradient-to-br ${cert.gradient}`}>
                  </div>
                </div>
                
                {/* Shine effect on hover */}
                <div className={`absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent transform -translate-x-full transition-transform duration-1000 ${activeCard === cert.id ? 'translate-x-full' : '-translate-x-full'}`}></div>
              </div>
            </GlassCard>
          ))}
        </div>
        
        {/* Extra decorative element */}
        <div className="flex justify-center mt-16">
          <div className={`w-20 h-1 bg-gradient-to-r from-violet-500/50 via-blue-500/50 to-violet-500/50 rounded transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}></div>
        </div>
      </div>
    </section>
  );
};

export default CertificatesSection;
